import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";
import html2pdf from "html2pdf.js/dist/html2pdf.min.js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Athrv from "../../img/athrv-cloud-logo-1.png";
import AthrvDark from "../../img/Athrv-white.png";
import Mermaid from "./Mermaid";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthProvider";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
// Highlight keywords in code blocks
const highlightCode = (code, isDarkMode) => {
  const keywords = [
    "const",
    "let",
    "var",
    "return",
    "if",
    "else",
    "for",
    "while",
    "void",
    "int",
    "float",
    "double",
    "bool",
    "std",
    "function",
    "import",
    "export",
    "class",
    "new",
    "await",
    "async",
    "private",
    "public",
    "protected",
    "struct",
  ];
  const keywordRegex = new RegExp(`\\b(${keywords.join("|")})\\b`, "g");

  const html = code
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(keywordRegex, '<span style="color: green;">$1</span>');

  const backgroundColor = isDarkMode ? "#2e2e2e" : "#f5f5f5";
  return `<pre style="background-color: ${backgroundColor}; padding: 10px;overflow-wrap: anywhere;">${html}</pre>`;
};

const AiAnalysis = ({ analysisResult }) => {
  const { projectId } = useParams();
  const { authToken, organizationId } = useAuth();
  const [isFetching, setIsFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const location = useLocation();
  const activeTab = location.state?.activeTab;
  const [analysisStatus, setAnalysisStatus] = useState("invoked");
  const [statusIcon, setStatusIcon] = useState(null);
  const [report, setReport] = useState("");
  const analysisRef = useRef();
  const isPollingCancelled = useRef(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const CheckmarkIcon = () => (
    <svg
      className="checkmark"
      width="17"
      height="13"
      viewBox="0 0 17 13"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polyline
        points="1 7 5 11 15 1"
        stroke="#4caf50"
        strokeWidth="2"
        fill="none"
        strokeDasharray="20"
        strokeDashoffset="20"
      >
        <animate
          attributeName="stroke-dashoffset"
          from="20"
          to="0"
          dur="0.5s"
          begin="0.2s"
          fill="freeze"
        />
      </polyline>
    </svg>
  );
  const WaveLoadingIcon = ({ color = "#ffffff" }) => (
    <div className="icon">
      <svg
        version="1.1"
        id="L7"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        x="0px"
        y="0px"
        viewBox="0 0 100 100"
        xmlSpace="preserve"
      >
        <path
          fill={color}
          d="M31.6,3.5C5.9,13.6-6.6,42.7,3.5,68.4c10.1,25.7,39.2,38.3,64.9,28.1l-3.1-7.9c-21.3,8.4-45.4-2-53.8-23.3
      c-8.4-21.3,2-45.4,23.3-53.8L31.6,3.5z"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            dur="2s"
            from="0 50 50"
            to="360 50 50"
            repeatCount="indefinite"
          />
        </path>
        <path
          fill={color}
          d="M42.3,39.6c5.7-4.3,13.9-3.1,18.1,2.7c4.3,5.7,3.1,13.9-2.7,18.1l4.1,5.5c8.8-6.5,10.6-19,4.1-27.7
      c-6.5-8.8-19-10.6-27.7-4.1L42.3,39.6z"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            dur="1s"
            from="0 50 50"
            to="-360 50 50"
            repeatCount="indefinite"
          />
        </path>
        <path
          fill={color}
          d="M82,35.7C74.1,18,53.4,10.1,35.7,18S10.1,46.6,18,64.3l7.6-3.4c-6-13.5,0-29.3,13.5-35.3s29.3,0,35.3,13.5
      L82,35.7z"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            dur="2s"
            from="0 50 50"
            to="360 50 50"
            repeatCount="indefinite"
          />
        </path>
      </svg>
    </div>
  );
  const pollAnalysisStatus = () => {
    const token = localStorage.getItem("access_token");

    const centeredWrapper = (icon) => (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "150px",
          width: "100%",
        }}
      >
        {icon}
      </div>
    );

    const intervalId = setInterval(async () => {
      if (isPollingCancelled.current) return;

      try {
        const requestBody = { project_id: projectId };
        const statusResponse = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/ai-agent/pre-analysis-status`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (isPollingCancelled.current) return;

        const statusData = await statusResponse.json();
        const processStatus = statusData?.pre_ai_analysis_process;
        const analysisStatus = statusData?.pre_ai_analysis_status;
        const resultUrl = statusData?.latest_ai_analysis_report;

        setAnalysisStatus(processStatus);

        if (processStatus === "invoked" || analysisStatus === "initialized") {
          setStatusIcon(centeredWrapper(<WaveLoadingIcon color="#FFEB3B" />));
        } else if (analysisStatus === "in-progress") {
          setStatusIcon(centeredWrapper(<WaveLoadingIcon color="#2196F3" />));
        } else if (processStatus === "completed") {
          setStatusIcon(centeredWrapper(<CheckmarkIcon />));

          if (resultUrl) {
            const resultResponse = await fetch(resultUrl);
            const resultText = await resultResponse.text();
            setAiResult(resultText);
          }

          clearInterval(intervalId);
        }
      } catch (err) {
        console.error("Polling error:", err);
        clearInterval(intervalId);
      }
    }, 8000);

    return intervalId;
  };
  useEffect(() => {
    let intervalId;

    if (activeTab === 1) {
      isPollingCancelled.current = false;
      intervalId = pollAnalysisStatus();
    }

    return () => {
      isPollingCancelled.current = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [activeTab]);

  const handleCurrentAIAnalysis = async () => {
    setIsSubmitting(true);
    const token = localStorage.getItem("access_token");
  
    try {
      setIsFetching(true);
  
      const requestBody = {
        project_id: projectId,
        organization_id: organizationId,
        jwt_access_token: token,
        service_mode: "ai-pre-analysis",
      };
  
      const apiUrl = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/ai-agent/pre-analysis`;
      const analysisResponse = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
  
      if (!analysisResponse.ok) {
        throw new Error("Failed to start analysis");
      }
  
      const analysisData = await analysisResponse.json();
  
      if (analysisData.notification_message) {
        toast.info(analysisData.notification_message);
      }
      
      // IMPORTANT: Reset the cancellation flag before starting polling
      isPollingCancelled.current = false;
      pollAnalysisStatus();
    } catch (err) {
      console.error("Analysis error:", err);
      alert("Failed to analyze the project.");
    } finally {
      setIsFetching(false);
      setIsSubmitting(false);
      setAnalysisStatus("invoked");
    }
  };

  const handleDownloadPDF = () => {
  setLoading(true);

  const original = analysisRef.current;
  const clone = original.cloneNode(true);
  clone.classList.add("force-light-mode");
  clone.style.backgroundColor = "white";
  clone.style.color = "black";
  clone.style.fontFamily = "Arial, sans-serif";
  clone.style.wordBreak = "break-word";
  clone.style.overflowWrap = "anywhere";
  clone.style.padding = "20px";

  // Replace logo if needed
  const logoImg = clone.querySelector('img[alt="Logo"]');
  if (logoImg) {
    logoImg.src = Athrv;
  }

  // Table styling
  const tables = clone.querySelectorAll("table");
  tables.forEach((table) => {
    table.style.borderCollapse = "collapse";
    table.style.width = "100%";
    table.style.tableLayout = "auto";
  });

  const tableHeaders = clone.querySelectorAll("th");
  tableHeaders.forEach((th) => {
    th.style.border = "1px solid #999";
    th.style.backgroundColor = "#f0f0f0";
    th.style.color = "black";
    th.style.padding = "6px";
    th.style.textAlign = "center";
    th.style.verticalAlign = "middle";
    th.style.whiteSpace = "normal";
    th.style.transform = "none";
    th.style.writingMode = "horizontal-tb";
    th.style.rotate = "none";
  });

  const tableCells = clone.querySelectorAll("td");
  tableCells.forEach((td) => {
    td.style.border = "1px solid #999";
    td.style.padding = "6px";
    td.style.textAlign = "left";
    td.style.verticalAlign = "top";
    td.style.whiteSpace = "normal";
    td.style.transform = "none";
    td.style.writingMode = "horizontal-tb";
    td.style.rotate = "none";
  });

  // Code block styling (including pre, code, hljs etc.)
  const codeBlocks = clone.querySelectorAll("pre, code, td pre, td code, .hljs, .code-block");
  codeBlocks.forEach((el) => {
    el.style.whiteSpace = "pre-wrap";
    el.style.wordBreak = "break-word";
    el.style.overflow = "auto";
    el.style.maxWidth = "100%";
    el.style.fontSize = "11px";
    el.style.backgroundColor = "white";
    el.style.color = "black";
    el.style.border = "1px solid #ccc";
    el.style.fontFamily = "Arial, sans-serif";
    el.style.padding = "6px";
    el.style.borderRadius = "4px";
  });

  // Inject style block to force light mode for code globally
  const style = document.createElement("style");
  style.innerHTML = `
    pre, code, .hljs, .code-block {
      background-color: white !important;
      color: black !important;
      border: 1px solid #ccc;
      font-family: Arial, sans-serif;
      padding: 6px;
      border-radius: 4px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  `;
  clone.appendChild(style);

  // Wrap and render
  const wrapper = document.createElement("div");
  wrapper.style.position = "fixed";
  wrapper.style.top = "0";
  wrapper.style.left = "-9999px";
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  setTimeout(() => {
    html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: "ai-analysis.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      })
      .from(clone)
      .save()
      .then(() => {
        document.body.removeChild(wrapper);
        setLoading(false);
      })
      .catch(() => {
        document.body.removeChild(wrapper);
        setLoading(false);
      });
  }, 100);
};
  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleCurrentAIAnalysis}
                disabled={isFetching}
                sx={{
                  backgroundColor: isDarkMode ? "#0066CC" : "#0C1A32",
                  "&:hover": { backgroundColor: "#002244" },
                  minWidth: 200,
                  height: 40,
                }}
              >
                AI Analysis
              </Button>

              <Tooltip
                title={
                  <Box sx={{ p: 2, maxWidth: 400 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <Typography
                        variant="body1"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        What's happening here?
                      </Typography>
                      Our AI conducted a comprehensive code analysis examining
                      your code's architecture, security vulnerabilities,
                      performance issues, MISRA compliance, build system, and
                      overall quality - then generated a detailed professional
                      report.
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                      The report includes:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 1 }}>
                      <li>
                        <strong>Executive Summary -</strong> with key findings
                      </li>
                      <li>
                        <strong>Report Card with -</strong> letter grades (A-F)
                        for major areas
                      </li>
                      <li>
                        <strong>Architecture Analysis -</strong> How your code
                        is structured and organized
                      </li>
                      <li>
                        <strong>Security & Bug Detection -</strong>{" "}
                        Vulnerabilities and potential issues
                      </li>
                      <li>
                        <strong>MISRA Compliance Check -</strong>{" "}
                        Safety-critical coding standards evaluation
                      </li>
                      <li>
                        <strong>Performance Assessment -</strong> Speed and
                        efficiency analysis
                      </li>
                      <li>
                        <strong>Build System Review -</strong> Compilation and
                        dependency analysis
                      </li>
                    </Box>

                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Grade Scale:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                      <li>
                        <strong>A:</strong> Excellent - Industry best practices
                      </li>
                      <li>
                        <strong>B:</strong> Good - Minor improvements needed
                      </li>
                      <li>
                        <strong>C:</strong> Average - Some issues to address
                      </li>
                      <li>
                        <strong>D:</strong> Below Average - Multiple problems
                      </li>
                      <li>
                        <strong>F:</strong> Failing - Critical issues requiring
                        immediate fixes
                      </li>
                    </Box>
                  </Box>
                }
                placement="right"
                arrow
              >
                <InfoOutlinedIcon
                  sx={{ color: "#1976d2", cursor: "pointer" }}
                />
              </Tooltip>
            </Box>

            {analysisStatus && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0.5 }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
                >
                  {/* {analysisStatus == "invoked" ? "Processing..." : ""} */}
                  {/* {analysisStatus === "invoked" ? (
                    <CircularProgress
                      size={24}
                      sx={{ color: isDarkMode ? "#0066CC" : "#0C1A32" }}
                    />
                  ) : null} */}
                </Typography>
                {/* {statusIcon} */}
              </Box>
            )}
          </Box>

          {(aiResult || analysisResult) && (
            <Button
              variant="outlined"
              onClick={handleDownloadPDF}
              disabled={loading}
              sx={{
                borderColor: "#0C1A32",
                color: isDarkMode ? "#ffffff" : "#0C1A32",
                backgroundColor: isDarkMode ? "#0066CC" : "inherit",
                "&:hover": {
                  borderColor: "#002244",
                  backgroundColor: "#f0f0f0",
                },
                minWidth: 200,
                height: 40,
                position: "relative",
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "#0C1A32" }} />
              ) : (
                "Download PDF"
              )}
            </Button>
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          AI Analysis Result
        </Typography>

        <Box
          ref={analysisRef}
          className="analysis-container"
          sx={{
            mt: 2,
            border: "1px solid #ccc",
            p: 2,
            borderRadius: "8px",
            overflowX: "auto",
            position: "relative",
            wordBreak: "break-word",
          }}
        >
          <Box
            component="img"
            src={theme.palette.mode === "dark" ? AthrvDark : Athrv}
            alt="Logo"
            crossOrigin="anonymous"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 150,
              height: "auto",
              zIndex: 10,
            }}
          />

          {analysisStatus === "invoked" ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress
                size={48}
                sx={{ color: isDarkMode ? "#0066CC" : "#0C1A32" }}
              />
            </Box>
          ) : analysisStatus === "completed" && (aiResult || analysisResult) ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: (props) => (
                  <Typography
                    fontWeight="bold"
                    gutterBottom
                    fontSize="1.3rem"
                    {...props}
                  />
                ),
                h2: (props) => (
                  <Typography
                    fontWeight="bold"
                    gutterBottom
                    fontSize="1.2rem"
                    {...props}
                  />
                ),
                h3: (props) => (
                  <Typography
                    fontWeight="bold"
                    gutterBottom
                    fontSize="1.1rem"
                    {...props}
                  />
                ),
                h4: (props) => (
                  <Typography
                    fontWeight="bold"
                    gutterBottom
                    fontSize="0.9rem"
                    {...props}
                  />
                ),
                h5: (props) => (
                  <Typography
                    fontWeight="bold"
                    gutterBottom
                    fontSize="0.8rem"
                    {...props}
                  />
                ),
                h6: (props) => (
                  <Typography
                    fontWeight="bold"
                    gutterBottom
                    fontSize="0.7rem"
                    {...props}
                  />
                ),
                p: (props) => (
                  <Typography variant="body1" gutterBottom {...props} />
                ),
                ul: (props) => (
                  <ul
                    style={{ paddingLeft: "20px", marginBottom: "16px" }}
                    {...props}
                  />
                ),
                ol: (props) => (
                  <ol
                    style={{ paddingLeft: "20px", marginBottom: "16px" }}
                    {...props}
                  />
                ),
                li: (props) => (
                  <li style={{ marginBottom: "4px" }} {...props} />
                ),
                table: (props) => (
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      borderCollapse: "collapse",
                      my: 2,
                      fontSize: "14px",
                      overflowWrap: "anywhere",
                    }}
                    {...props}
                  />
                ),
                th: (props) => (
                  <Box
                    component="th"
                    sx={{
                      border: "1px solid #999",
                      padding: "8px",
                      backgroundColor: isDarkMode ? "#585858" : "#f5f5f5",
                      color: isDarkMode ? "#ffffff" : "inherit",
                      textAlign: "left",
                    }}
                    {...props}
                  />
                ),
                td: (props) => (
                  <Box
                    component="td"
                    sx={{
                      border: "1px solid #ccc",
                      padding: "8px",
                      textAlign: "left",
                    }}
                    {...props}
                  />
                ),
                code({ inline, children, className, ...props }) {
                  const codeString = String(children).trim();
                  const language = className?.replace("language-", "");

                  if (!inline && language === "mermaid") {
                    return <Mermaid chart={codeString} />;
                  }

                  if (inline) {
                    return (
                      <code
                        style={{
                          backgroundColor: isDarkMode ? "#585858" : "#f5f5f5",
                          color: isDarkMode ? "#9fef9f" : "green",
                          padding: "2px 4px",
                          overflowWrap: "anywhere",
                        }}
                        {...props}
                      >
                        {codeString}
                      </code>
                    );
                  }

                  return (
                    <Box
                      component="pre"
                      className="code-block"
                      sx={{
                        mt: 2,
                        backgroundColor: isDarkMode ? "#383838" : "#f5f5f5",
                        color: isDarkMode ? "#ffffff" : "#000000",
                        p: 2,
                        overflowX: "auto",
                        "& code": {
                          backgroundColor: "transparent !important",
                          color: "inherit !important",
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: highlightCode(codeString, isDarkMode),
                      }}
                    />
                  );
                },
              }}
            >
              {aiResult || analysisResult}
            </ReactMarkdown>
          ) : (
            <Typography color="textSecondary">
              No analysis result to display.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AiAnalysis;
